import { TinyEmitter } from 'tiny-emitter'

import type { Site } from '@@/types/plugins'
import Utils from '@/utils'

export default defineNuxtPlugin((nuxtApp) => {
  const headers = useRequestHeaders()
  const locationStore = useLocationStore()
  const layoutStore = useLayoutStore()

  const emitter = new TinyEmitter()

  const site: Site = {
    events: {
      $on: <T extends unknown[]>(event: string, callback: (...args: T) => void) => emitter.on(event, callback),
      $once: <T extends unknown[]>(event: string, callback: (...args: T) => void) => emitter.once(event, callback),
      $off: <T extends unknown[]>(event: string, callback?: (...args: T) => void) => emitter.off(event, callback),
      $emit: <T extends unknown[]>(event: string, ...args: T) => emitter.emit(event, ...args)
    },
    get location() {

      if (!locationStore.location) return null

      const location = { ...locationStore.location }

      location.mapUrl = ({ size, zoom, scale = 2 } = {}) => {
        scale = (scale > 1) ? 2 : 1

        let url = ((process.env.DEPLOY_CDN) ? process.env.DEPLOY_CDN : '') + '/api/location/mapTile?site.location=' + location.id

        if (size) {
          url += '&size=' + size
        }
        if (zoom) {
          url += '&zoom=' + zoom
        }
        if (scale) {
          url += '&scale=' + scale
        }
        if (this.colors.map.style) {
          if (this.colors.map.style === 'dark') {
            url += '&style=dark'
          } else if (this.colors.map.style === 'light') {
            url += '&style=light'
          } else {
            url += '&style=default'
          }
        }
        if (location.modified) {
          url += '&ts=' + location.modified
        }

        return url
      }

      return location
    },
    get images() {
      if (!locationStore.images) return null

      const images = { ...locationStore.images }

      images.heroImageCssUrl = (size) => {
        if (images.hero) {
          return 'url(\'' + images.hero[size] + '\')'
        }
        return 'none'
      }

      images.logoImageCssUrl = (size) => {
        if (images.logo) {
          return 'url(\'' + images.logo[size] + '\')'
        }
        return 'none'
      }

      return images
    },
    get settings() {
      return locationStore.settings
    },
    get integrations() {
      return locationStore.integrations
    },
    get menu() {
      return locationStore.menu
    },
    get customPages() {
      return locationStore.customPages
    },
    get domain() {
      return locationStore.domain
    },
    get tabs() {
      if (!locationStore.tabs) return null

      const tabs = { ...locationStore.tabs }

      tabs.isEmpty = (Object.keys(tabs).length === 0)

      tabs.getName = (tab, defaultName) => {
        if (tabs[tab]) {
          return tabs[tab].name
        }

        return defaultName || null
      }

      tabs.getInfo = (tab) => {
        if (tabs[tab]) {
          return tabs[tab].information
        }

        return null
      }

      tabs.getGiftCardImage = (image) => {
        if (tabs.giftCard && tabs.giftCard.images) {
          for (let i = 0; i < tabs.giftCard.images.length; i++) {
            const giftCardImage = tabs.giftCard.images[i]
            if (giftCardImage.id == image) {
              return giftCardImage
            }
          }
        }

        return null
      }

      return tabs
    },
    get onlineBookingEnabled() {
      return (this.tabs.book || this.tabs.timetable || this.tabs.lastMinute)
    },
    get host() {
      if (headers?.host) {
        return headers.host
      } else if (window?.location) {
        return window.location.hostname
      }
      return null
    },
    get slug() {
      const reqDomain = this.host

      const config = useNuxtApp().$config

      if (config.public?.domain && reqDomain.includes(config.public.domain)) {
        const domainParts = reqDomain.split('.')

        if (domainParts.length > 2) {
          return domainParts[0]
        }
      } else {
        return reqDomain.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()
      }
    },
    get colors() {
      const colours = locationStore.colours

      if (colours[this.layout]) {
        return colours[this.layout]
      }

      return colours.site
    },
    get homeRoute() {
      const layout = this.layout
      if (layout === 'bot') {
        return { path: '/bot' }
      } else {
        return { path: '/' }
      }
    },
    get layout() {
      const layout = layoutStore.layout

      if (layout === 'bot') {
        return 'bot'
      } else if (layout === 'widget') {
        return 'widget'
      } else if (layout === 'facebook') {
        return 'facebook'
      } else {
        return 'site'
      }
    },
    get isEmbed() {
      return Utils.browser.inIframe() && ['widget', 'bot'].includes(this.layout) // 
    },
    get isGoogleAnalyticsEnabled() {
      return this.integrations['1d8fbf495ebabf8c5cad1a8c2bf97662']?.enabled && this.integrations['1d8fbf495ebabf8c5cad1a8c2bf97662']?.data?.trackingID
    }
  }

  if (import.meta.client && Utils.browser.inIframe()) {
    const layout = layoutStore.layout

    if (!layout && window?.parent) {

      window.postRobot?.send(window.parent, 'bookapp.layout.get').then((event : any) => {
        if (event.data && event.data.layout) {
          layoutStore.layout = event.data.layout
        }
      }).catch(function (err: any) {
        // console.log(err)
        layoutStore.layout = 'site'
      })
    }
  }

  // @ts-ignore
  nuxtApp.$i18n.baseUrl = useRequestURL().origin;

  nuxtApp.provide('site', site)
})
